import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faPause,
  faForward,
  faBackward,
  faVolumeHigh,
  faVolumeLow,
  faVolumeXmark,
  faRepeat,
  faRandom,
  faStepForward,
  faStepBackward,
} from "@fortawesome/free-solid-svg-icons";

//dance gifs
import dance_1 from "./Gifs/dance_1.gif";
import dance_2 from "./Gifs/dance_2.gif";
import dance_3 from "./Gifs/dance_3.gif";
import dance_4 from "./Gifs/dance_4.gif";
import dance_5 from "./Gifs/dance_5.gif";

const MusicPlayer = ({ src, fileName = "Unknown Track", autoPlay = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLooping, setIsLooping] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [visualizerData, setVisualizerData] = useState([]);
  const [danceMove, setDanceMove] = useState(0);

  const audioRef = useRef(null);
  const progressBarRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationFrameRef = useRef(null);
  const dance_move = [dance_1, dance_2, dance_3, dance_4, dance_5];

  // Initialize audio context and analyzer for visualization
  const sourceRef = useRef(null);
  useEffect(() => {
    if (!audioRef.current) return;

    // only create ONCE
    if (audioContextRef.current && sourceRef.current) return;

    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const ctx = new AudioContext();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;

    const bufferLength = analyser.frequencyBinCount;
    dataArrayRef.current = new Uint8Array(bufferLength);

    let source;
    try {
      source = ctx.createMediaElementSource(audioRef.current);
      source.connect(analyser);
      analyser.connect(ctx.destination);

      audioContextRef.current = ctx;
      analyserRef.current = analyser;
      sourceRef.current = source;
    } catch (error) {
      console.error("Error creating audio source:", error);
    }

    // 👇 DO NOT close or disconnect the source/context here
    // React StrictMode will call this twice in dev and break things.
    // Just let the context live for the lifetime of the page.
  }, []);

  // Visualizer animation loop
  // Visualizer animation loop
  useEffect(() => {
    if (!analyserRef.current) return;

    const animate = () => {
      if (analyserRef.current && dataArrayRef.current) {
        analyserRef.current.getByteFrequencyData(dataArrayRef.current);

        const bars = 40;
        const newData = [];

        // Get frequency data more accurately
        for (let i = 0; i < bars; i++) {
          // Sample across different frequency ranges for better music response
          const frequencyIndex = Math.floor(
            (i / bars) * dataArrayRef.current.length
          );
          let value = dataArrayRef.current[frequencyIndex];

          // Boost lower frequencies (bass) and higher frequencies (treble) for better visualization
          if (i < 15) {
            // Bass frequencies
            value = value * 1.5;
          } else if (i > 45) {
            // Treble frequencies
            value = value * 1.2;
          }

          // Cap the value
          value = Math.min(255, value);
          newData.push(value);
        }

        setVisualizerData(newData);
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(animate);
    } else {
      // When paused, gradually reduce values to zero
      const reduceValues = () => {
        setVisualizerData((prev) => {
          const reduced = prev.map((val) => Math.max(0, val * 0.7));
          const allZero = reduced.every((val) => val < 5);

          if (!allZero && !isPlaying) {
            setTimeout(reduceValues, 50);
          }

          return allZero ? Array(60).fill(0) : reduced;
        });
      };

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      reduceValues();
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isPlaying]);

  // Update current time
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener("timeupdate", updateTime);
    audio.addEventListener("loadedmetadata", updateDuration);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", updateTime);
      audio.removeEventListener("loadedmetadata", updateDuration);
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  // Play/Pause

  const togglePlay = async () => {
    if (audioContextRef.current?.state === "suspended") {
      await audioContextRef.current.resume();
    }

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }

    setIsPlaying(!isPlaying);
    setDanceMove(Math.floor(Math.random() * dance_move.length));
  };

  // Seek
  const handleSeek = (e) => {
    const rect = progressBarRef.current.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Volume control
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    audioRef.current.volume = newVolume;
    if (newVolume > 0) setIsMuted(false);
  };

  // Mute toggle
  const toggleMute = () => {
    setIsMuted(!isMuted);
    audioRef.current.muted = !isMuted;
  };

  // Speed control
  const handleSpeedChange = (speed) => {
    setPlaybackRate(speed);
    audioRef.current.playbackRate = speed;
  };

  // Skip forward/backward
  const skip = (seconds) => {
    const newTime = Math.max(0, Math.min(duration, currentTime + seconds));
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Format time
  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  // Get volume icon
  const getVolumeIcon = () => {
    if (isMuted || volume === 0) return faVolumeXmark;
    if (volume < 0.5) return faVolumeLow;
    return faVolumeHigh;
  };

  const containerStyle = {
    width: "100%",
    height: "100%",
    backgroundColor: "#1a1a2e",
    display: "flex",
    flexDirection: "column",
    color: "#fff",
    fontFamily: "Arial, sans-serif",
  };

  const visualizerContainerStyle = {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    background:
      "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
    position: "relative",
    overflow: "hidden",
  };

  const visualizerStyle = {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    gap: "3px",
    height: "200px",
    width: "100%",
    maxWidth: "500px",
  };

  const controlsContainerStyle = {
    backgroundColor: "#16213e",
    padding: "20px",
    borderTop: "2px solid #0f3460",
  };

  const infoStyle = {
    textAlign: "center",
    marginBottom: "15px",
  };

  const titleStyle = {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: "5px",
    color: "#00d4ff",
  };

  const progressContainerStyle = {
    marginBottom: "15px",
  };

  const progressBarStyle = {
    width: "100%",
    height: "6px",
    backgroundColor: "#0f3460",
    borderRadius: "3px",
    cursor: "pointer",
    position: "relative",
  };

  const progressFillStyle = {
    height: "100%",
    backgroundColor: "#00d4ff",
    borderRadius: "3px",
    width: `${(currentTime / duration) * 100}%`,
    transition: "width 0.1s linear",
  };

  const timeDisplayStyle = {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "12px",
    color: "#aaa",
    marginTop: "5px",
  };

  const mainControlsStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "15px",
    marginBottom: "15px",
  };

  const buttonStyle = {
    background: "none",
    border: "none",
    color: "#fff",
    fontSize: "20px",
    cursor: "pointer",
    padding: "10px",
    borderRadius: "50%",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const playButtonStyle = {
    ...buttonStyle,
    fontSize: "28px",
    backgroundColor: "#00d4ff",
    color: "#1a1a2e",
    width: "50px",
    height: "50px",
  };

  const secondaryControlsStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "10px",
  };

  const controlGroupStyle = {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  };

  const sliderStyle = {
    width: "80px",
    height: "4px",
    borderRadius: "2px",
    outline: "none",
    WebkitAppearance: "none",
    background: "#0f3460",
  };

  const speedButtonStyle = {
    ...buttonStyle,
    fontSize: "12px",
    padding: "5px 10px",
    borderRadius: "4px",
    backgroundColor: "#0f3460",
    minWidth: "45px",
  };

  return (
    <div style={containerStyle}>
      <audio
        ref={audioRef}
        src={src}
        loop={isLooping}
        onError={(e) => console.error("Audio error:", e)}
      />

      {/* Visualizer */}
      <div style={visualizerContainerStyle}>
        {/* Animated background particles */}
        {/* <div
          style={{
            position: "absolute",
            width: "100%",
            height: "100%",
            background: isPlaying
              ? "radial-gradient(circle at 50% 50%, rgba(0, 212, 255, 0.1) 0%, transparent 70%)"
              : "none",
            animation: isPlaying ? "pulse 2s ease-in-out infinite" : "none",
          }}
        /> */}

        <div
          style={{
            position: "relative",
            width: "350px",
            height: "350px",
          }}
        >
          <svg width="500" height="500" viewBox="0 0 500 500">
            {/* Background circle */}
            <circle
              cx="175"
              cy="175"
              r="140"
              fill="none"
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="2"
            />

            {/* Visualizer bars - PROPER CIRCULAR LAYOUT */}
            {(visualizerData.length > 0
              ? visualizerData
              : Array(40).fill(0)
            ).map((value, index) => {
              const angle = (index / 40) * 360;
              const barHeight = isPlaying ? (value / 255) * 70 + 10 : 10;
              const hue = (angle + currentTime * 30) % 360;

              // Calculate position for outward-growing bars in a circle
              const startRadius = 100; // Inner radius where bars start
              const endRadius = startRadius + barHeight; // Outer radius where bars end

              const startAngle = (angle * Math.PI) / 180;
              const endAngle = startAngle;

              const x1 = 175 + startRadius * Math.cos(startAngle);
              const y1 = 175 + startRadius * Math.sin(startAngle);
              const x2 = 175 + endRadius * Math.cos(endAngle);
              const y2 = 175 + endRadius * Math.sin(endAngle);

              return (
                <line
                  key={index}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={`hsl(${hue}, 80%, 60%)`}
                  strokeWidth="9" // Thick bars
                  strokeLinecap="round" // Rounded ends
                  opacity={isPlaying ? 0.9 : 0.3}
                >
                  {isPlaying && (
                    <animate
                      attributeName="y2"
                      values={`${y2}`}
                      dur="0.15s"
                      repeatCount="indefinite"
                      calcMode="spline"
                      keySplines="0.4 0 0.2 1; 0.4 0 0.2 1"
                    />
                  )}
                </line>
              );
            })}

            {/* Pulsing outer ring that responds to average volume */}
            <circle
              cx="175"
              cy="175"
              r="160"
              fill="none"
              stroke="rgba(0, 212, 255, 0.3)"
              strokeWidth="1"
              opacity={isPlaying ? 0.5 : 0}
            >
              {isPlaying && (
                <animate
                  attributeName="r"
                  values="160;170;160"
                  dur="1s"
                  repeatCount="indefinite"
                />
              )}
            </circle>

            {/* Gradient definitions */}
            <defs>
              <radialGradient id="centerGradient">
                <stop offset="0%" stopColor="#00d4ff" />
                <stop offset="100%" stopColor="transparent" />
              </radialGradient>
            </defs>
          </svg>
        </div>
        {/* Center play indicator */}
        {isPlaying && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "150px",
              height: "150px",
              borderRadius: "50%",
              // backgroundColor: isPlaying
              //   ? "rgba(0, 212, 255, 0.3)"
              //   : "rgba(255, 255, 255, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              //border: "2px solid rgba(255, 255, 255, 0.3)",
              //transition: "all 0.3s ease",
              //boxShadow: isPlaying ? "0 0 20px rgba(0, 212, 255, 0.5)" : "none",
            }}
          >
            {/* <FontAwesomeIcon
            icon={isPlaying ? faPause : faPlay}
            style={{
              fontSize: "24px",
              color: "#00d4ff",
              marginLeft: isPlaying ? "0" : "3px",
            }}
          /> */}
            <img
              src={dance_move[danceMove]}
              style={{
                height: "120px",
                width: "120px",
                objectFit: "fill",
              }}
            />
          </div>
        )}
      </div>

      {/* Controls */}
      <div style={controlsContainerStyle}>
        {/* Track Info */}
        <div style={infoStyle}>
          <div style={titleStyle}>{fileName}</div>
        </div>

        {/* Progress Bar */}
        <div style={progressContainerStyle}>
          <div
            ref={progressBarRef}
            style={progressBarStyle}
            onClick={handleSeek}
          >
            <div style={progressFillStyle} />
          </div>
          <div style={timeDisplayStyle}>
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Main Controls */}
        <div style={mainControlsStyle}>
          <button
            style={buttonStyle}
            onClick={() => skip(-10)}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#0f3460")}
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "transparent")
            }
            title="Skip -10s"
          >
            <FontAwesomeIcon icon={faBackward} />
          </button>

          <button
            style={buttonStyle}
            onClick={() => skip(-5)}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#0f3460")}
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "transparent")
            }
            title="Skip -5s"
          >
            <FontAwesomeIcon icon={faStepBackward} />
          </button>

          <button
            style={playButtonStyle}
            onClick={togglePlay}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#00b8d4")}
            onMouseLeave={(e) => (e.target.style.backgroundColor = "#00d4ff")}
          >
            <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
          </button>

          <button
            style={buttonStyle}
            onClick={() => skip(5)}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#0f3460")}
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "transparent")
            }
            title="Skip +5s"
          >
            <FontAwesomeIcon icon={faStepForward} />
          </button>

          <button
            style={buttonStyle}
            onClick={() => skip(10)}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#0f3460")}
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "transparent")
            }
            title="Skip +10s"
          >
            <FontAwesomeIcon icon={faForward} />
          </button>
        </div>

        {/* Secondary Controls */}
        <div style={secondaryControlsStyle}>
          {/* Volume Control */}
          <div style={controlGroupStyle}>
            <button
              style={buttonStyle}
              onClick={toggleMute}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#0f3460";
                setShowVolumeSlider(true);
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
                setTimeout(() => setShowVolumeSlider(false), 1000);
              }}
              title="Mute/Unmute"
            >
              <FontAwesomeIcon icon={getVolumeIcon()} />
            </button>
            {
              /*showVolumeSlider &&*/ <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={volume}
                onChange={handleVolumeChange}
                style={sliderStyle}
              />
            }
          </div>

          {/* Speed Control */}
          <div style={controlGroupStyle}>
            <span style={{ fontSize: "12px", color: "#aaa" }}>Speed:</span>
            {[0.5, 0.75, 1, 1.25, 1.5, 2].map((speed) => (
              <button
                key={speed}
                style={{
                  ...speedButtonStyle,
                  backgroundColor:
                    playbackRate === speed ? "#00d4ff" : "#0f3460",
                  color: playbackRate === speed ? "#1a1a2e" : "#fff",
                }}
                onClick={() => handleSpeedChange(speed)}
                onMouseEnter={(e) => {
                  if (playbackRate !== speed) {
                    e.target.style.backgroundColor = "#1a4d6d";
                  }
                }}
                onMouseLeave={(e) => {
                  if (playbackRate !== speed) {
                    e.target.style.backgroundColor = "#0f3460";
                  }
                }}
              >
                {speed}x
              </button>
            ))}
          </div>

          {/* Loop Control */}
          <button
            style={{
              ...buttonStyle,
              backgroundColor: isLooping ? "#00d4ff" : "transparent",
              color: isLooping ? "#1a1a2e" : "#fff",
            }}
            onClick={() => setIsLooping(!isLooping)}
            onMouseEnter={(e) => {
              if (!isLooping) e.target.style.backgroundColor = "#0f3460";
            }}
            onMouseLeave={(e) => {
              if (!isLooping) e.target.style.backgroundColor = "transparent";
            }}
            title="Loop"
          >
            <FontAwesomeIcon icon={faRepeat} />
          </button>
        </div>
      </div>

      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 0.3; transform: scale(1); }
            50% { opacity: 0.6; transform: scale(1.05); }
          }
          
          input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: #00d4ff;
            cursor: pointer;
          }
          
          input[type="range"]::-moz-range-thumb {
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: #00d4ff;
            cursor: pointer;
            border: none;
          }
        `}
      </style>
    </div>
  );
};

export default MusicPlayer;
